import React, { useState, useRef, useEffect } from 'react';
import { useToastStore } from '../stores/toastStore';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { api, API_BASE, logHttpError } from '../services/api';
import AppearanceSettings from './settings/AppearanceSettings';
import PetSettings from './settings/PetSettings';
import SecuritySettings from './settings/SecuritySettings';
import type {
  AcquireSecurityOperation,
  CompleteCurrentSessionRevoke,
  ReleaseSecurityOperation,
  SecurityOperation,
} from '../hooks/useSecurityOperationMutex';
import type {
  AcknowledgeRecoveryCodes,
  PendingRecoveryCodes,
  PublishRecoveryCodes,
} from './settings/recoveryCodeMemory';

type ContactPrivacy = 'everyone' | 'contacts' | 'nobody';

interface PrivacySettingsState {
  hide_wall: boolean;
  hide_avatar: boolean;
  hide_full_name: boolean;
  hide_birth_date: boolean;
  hide_email: boolean;
  message_privacy: ContactPrivacy;
  call_privacy: ContactPrivacy;
  show_online: boolean;
}

type HidePrivacyKey = 'hide_wall' | 'hide_avatar' | 'hide_full_name' | 'hide_birth_date' | 'hide_email';

const defaultPrivacySettings: PrivacySettingsState = {
  hide_wall: false,
  hide_avatar: false,
  hide_full_name: false,
  hide_birth_date: false,
  hide_email: false,
  message_privacy: 'everyone',
  call_privacy: 'everyone',
  show_online: true,
};

const PrivacySettings: React.FC = () => {
  const userId = localStorage.getItem('sharkly_user_id');
  const [settings, setSettings] = useState<PrivacySettingsState>(defaultPrivacySettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<keyof PrivacySettingsState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!userId) {
      setError('Пользователь не авторизован');
      setLoading(false);
      return () => { active = false; };
    }
    api.get(`/profile/privacy/${userId}`).then(res => {
      if (active) setSettings({ ...defaultPrivacySettings, ...res.data });
    }).catch((loadError: unknown) => {
      logHttpError('Ошибка загрузки приватности', loadError);
      if (active) setError('Не удалось загрузить настройки приватности');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [userId]);

  const persist = async <Key extends keyof PrivacySettingsState>(key: Key, value: PrivacySettingsState[Key]) => {
    const previous = settings[key];
    setSettings((current) => ({ ...current, [key]: value }));
    setSaving(key);
    setError(null);
    try {
      await api.post('/profile/privacy', { [key]: value });
    } catch (saveError: unknown) {
      logHttpError('Ошибка сохранения приватности', saveError);
      setSettings((current) => ({ ...current, [key]: previous }));
      setError('Не удалось сохранить настройку');
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div role="status" className="text-gray-500 text-sm py-4">Загрузка настроек...</div>;

  const items: { key: HidePrivacyKey; title: string; desc: string }[] = [
    { key: 'hide_wall', title: '🔒 Скрыть стену', desc: 'Другие пользователи не смогут писать на вашей стене' },
    { key: 'hide_avatar', title: '🖼️ Скрыть аватар', desc: 'Аватар будет виден только вам' },
    { key: 'hide_full_name', title: '📛 Скрыть ФИО', desc: 'Настоящее имя будет скрыто от других' },
    { key: 'hide_birth_date', title: '🎂 Скрыть дату рождения', desc: 'Дата рождения не будет видна другим' },
    { key: 'hide_email', title: '📧 Скрыть почту', desc: 'Email будет скрыт от других пользователей' },
  ];

  return (
    <div className="space-y-3">
      <p className="text-gray-400 text-sm mb-4">Настройте, какую информацию о себе видят другие пользователи.</p>
      {error && <p role="alert" className="text-red-400 text-sm">{error}</p>}
      <div className="bg-[#15171c] rounded-xl border border-gray-800 p-4 space-y-4">
        <div>
          <label htmlFor="message-privacy" className="block text-white text-sm font-bold mb-2">Кто может писать сообщения</label>
          <select
            id="message-privacy"
            value={settings.message_privacy}
            disabled={saving !== null}
            onChange={(event) => void persist('message_privacy', event.target.value as ContactPrivacy)}
            className="w-full bg-[#0f1115] text-white border border-gray-700 rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none disabled:opacity-50"
          >
            <option value="everyone">Все</option>
            <option value="contacts">Контакты</option>
            <option value="nobody">Никто</option>
          </select>
        </div>
        <div>
          <label htmlFor="call-privacy" className="block text-white text-sm font-bold mb-2">Кто может звонить</label>
          <select
            id="call-privacy"
            value={settings.call_privacy}
            disabled={saving !== null}
            onChange={(event) => void persist('call_privacy', event.target.value as ContactPrivacy)}
            className="w-full bg-[#0f1115] text-white border border-gray-700 rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none disabled:opacity-50"
          >
            <option value="everyone">Все</option>
            <option value="contacts">Контакты</option>
            <option value="nobody">Никто</option>
          </select>
        </div>
        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <span>
            <span className="block text-white text-sm font-bold">Показывать, что я онлайн</span>
            <span className="block text-gray-500 text-xs mt-0.5">Разрешить другим видеть ваш статус присутствия</span>
          </span>
          <input
            type="checkbox"
            aria-label="Показывать, что я онлайн"
            checked={settings.show_online}
            disabled={saving !== null}
            onChange={(event) => void persist('show_online', event.target.checked)}
            className="w-5 h-5 accent-blue-600 shrink-0"
          />
        </label>
      </div>
      {items.map(item => (
        <button key={item.key} onClick={() => void persist(item.key, !settings[item.key])}
          disabled={saving !== null}
          aria-pressed={settings[item.key]}
          className="w-full flex items-center justify-between p-4 bg-[#15171c] rounded-xl border border-gray-800 hover:border-gray-700 transition text-left">
          <div>
            <p className="text-white text-sm font-bold">{item.title}</p>
            <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
          </div>
          <div className={`w-11 h-6 rounded-full transition-colors relative ${settings[item.key] ? 'bg-blue-600' : 'bg-gray-700'}`}>
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings[item.key] ? 'translate-x-5.5 left-0.5' : 'left-0.5'}`} />
          </div>
        </button>
      ))}
    </div>
  );
};

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  securityOperation: SecurityOperation | null;
  acquireSecurityOperation: AcquireSecurityOperation;
  releaseSecurityOperation: ReleaseSecurityOperation;
  onCurrentSessionRevoked: CompleteCurrentSessionRevoke;
  pendingRecoveryCodes: PendingRecoveryCodes | null;
  publishRecoveryCodes: PublishRecoveryCodes;
  acknowledgeRecoveryCodes: AcknowledgeRecoveryCodes;
}

type Tab = 'account' | 'privacy' | 'appearance' | 'notifications' | 'pet' | 'language' | 'security' | 'support';

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  onLogout,
  securityOperation,
  acquireSecurityOperation,
  releaseSecurityOperation,
  onCurrentSessionRevoked,
  pendingRecoveryCodes,
  publishRecoveryCodes,
  acknowledgeRecoveryCodes,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('account');
  const securityOperationLocked = securityOperation !== null;

  // Состояния и реф для загрузки баннера
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // Уведомления
  const notifEnabled = useToastStore(s => s.enabled);
  const setNotifEnabled = useToastStore(s => s.setEnabled);
  const [soundMessage, setSoundMessage] = useState(() => localStorage.getItem('sound_message') || 'default');
  const [soundLike, setSoundLike] = useState(() => localStorage.getItem('sound_like') || 'default');
  const [customSound, setCustomSound] = useState<string | null>(() => localStorage.getItem('custom_sound'));
  const { testSound } = useNotificationSound();

  useEffect(() => { localStorage.setItem('sound_message', soundMessage); }, [soundMessage]);
  useEffect(() => { localStorage.setItem('sound_like', soundLike); }, [soundLike]);

  const handleCustomSoundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      localStorage.setItem('custom_sound', dataUrl);
      setCustomSound(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  if (!isOpen) return null;

  const requestClose = () => {
    if (!securityOperationLocked) onClose();
  };

  // Закрытие по клику вне окна
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) requestClose();
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const userId = localStorage.getItem('sharkly_user_id');
    if (!userId) {
      alert("Пользователь не авторизован");
      return;
    }

    const formData = new FormData();
    formData.append('file', file); // ИСПРАВЛЕНО: file вместо files

    try {
      setUploadingBanner(true);

      const res = await api.post('/profile/banner', formData);

      setBannerPreview(res.data.banner_url);
    } catch (err: any) {
      logHttpError("Ошибка загрузки баннера", err);
      const errorMsg = err.response?.data?.detail || "Ошибка при загрузке баннера. Проверьте формат файла (PNG, JPEG, GIF, WEBP).";
      alert(errorMsg);
    } finally {
      setUploadingBanner(false);
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
    }
  };

  const logout = () => {
    if (securityOperationLocked) return;
    onLogout();
  };

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'account', label: 'Аккаунт', icon: '👤' },
    { id: 'privacy', label: 'Конфиденциальность', icon: '🔒' },
    { id: 'appearance', label: 'Оформление', icon: '' },
    { id: 'notifications', label: 'Уведомления', icon: '' },
    { id: 'pet', label: 'Питомец', icon: '🐾' },
    { id: 'language', label: 'Язык', icon: '🌐' },
    { id: 'security', label: 'Безопасность', icon: '🛡️' },
    { id: 'support', label: 'Вопросы по Sharkly', icon: '🦈' },
  ];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4 animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Настройки"
        aria-busy={securityOperationLocked}
        className="shark-panel shark-border w-full max-w-4xl h-[96vh] md:h-[80vh] rounded-2xl md:rounded-3xl border shadow-2xl flex flex-col md:flex-row overflow-hidden relative"
      >

        {/* Кнопка закрытия */}
        <button
          onClick={requestClose}
          disabled={securityOperationLocked}
          aria-label="Закрыть настройки"
          className="absolute top-3 right-3 md:top-4 md:right-4 text-gray-400 hover:text-white z-10 p-2 hover:bg-gray-800 rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ✕
        </button>

        {/* Сайдбар с категориями */}
        <aside className="shark-card shark-border shark-density-navbar w-full md:w-64 shrink-0 border-b md:border-b-0 md:border-r flex flex-col max-h-48 md:max-h-none overflow-y-auto">
          <h2 className="text-lg md:text-xl font-black text-white mb-3 md:mb-6 px-2 pr-10 flex items-center gap-2">⚙️ Настройки</h2>
          <nav aria-label="Разделы настроек" className="shark-density-list flex overflow-x-auto md:flex-col scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  if (!securityOperationLocked) setActiveTab(tab.id);
                }}
                disabled={securityOperationLocked}
                className={`shark-density-control shrink-0 md:w-full text-left rounded-xl flex items-center gap-2 md:gap-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                  activeTab === tab.id 
                    ? 'shark-accent-button text-white shadow-lg shadow-blue-900/20' 
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span className="font-medium text-sm">{tab.label}</span>
              </button>
            ))}
          </nav>

          <div className="mt-3 md:mt-auto pt-3 md:pt-6 border-t border-gray-800">
             <button
               onClick={logout}
               disabled={securityOperationLocked}
               className="shark-density-control w-full text-left rounded-xl flex items-center gap-3 text-red-400 hover:bg-red-500/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
             >
                <span>🚪</span>
                <span className="font-medium text-sm">Выйти из аккаунта</span>
             </button>
          </div>
        </aside>

        {/* Контентная часть */}
        <div role="region" aria-label="Содержимое настроек" className="shark-panel shark-density-settings min-h-0 flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              {tabs.find(t => t.id === activeTab)?.icon} {tabs.find(t => t.id === activeTab)?.label}
            </h3>

            {/* Рендер контента в зависимости от вкладки */}
            {activeTab === 'account' && (
              <div className="space-y-6">
                <div className="shark-card shark-border rounded-2xl border">
                  <h4 className="text-lg font-bold text-white mb-4">Личные данные</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wider font-bold mb-2">Имя пользователя</label>
                      <input type="text" defaultValue="test" className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wider font-bold mb-2">Email</label>
                      <input type="email" defaultValue="test@test.ru" className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none" />
                    </div>
                    <button className="shark-accent-button px-6 py-2 text-white rounded-xl font-bold transition">Сохранить изменения</button>
                  </div>
                </div>

              </div>
            )}

            {activeTab === 'privacy' && (
              <PrivacySettings />
            )}

            {activeTab === 'appearance' && (
              <div className="space-y-6">
                 {/* БЛОК ЗАГРУЗКИ БАННЕРА */}
                 <div className="shark-card shark-border rounded-2xl border">
                    <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      🖼️ Баннер профиля
                    </h4>
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                       <div className="w-full sm:w-64 h-32 bg-[#0f1115] rounded-xl border border-gray-700 overflow-hidden relative group flex items-center justify-center shrink-0">
                          {bannerPreview ? (
                            <img src={`${API_BASE}${bannerPreview}`} alt="Banner preview" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-gray-500 text-sm text-center px-4">Предпросмотр баннера</span>
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                             <span className="shark-overlay-label text-white text-xs font-bold">Нажмите "Выбрать файл"</span>
                          </div>
                       </div>

                       <div className="flex-1">
                          <p className="text-sm text-gray-400 mb-4">
                             Загрузите изображение для фона вашего мини-профиля и стены.
                             Это сделает ваш профиль уникальным!
                          </p>
                          <p className="text-xs text-gray-500 mb-4">
                             Поддерживаемые форматы: PNG, JPEG, GIF, WEBP.
                          </p>

                          <input
                            type="file"
                            ref={bannerInputRef}
                            className="hidden"
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            onChange={handleBannerUpload}
                          />

                          <button
                            onClick={() => bannerInputRef.current?.click()}
                            disabled={uploadingBanner}
                            className="shark-accent-button px-6 py-2 text-white rounded-xl font-bold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {uploadingBanner ? (
                              <>
                                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                                Загрузка...
                              </>
                            ) : (
                              'Выбрать файл'
                            )}
                          </button>
                       </div>
                    </div>
                 </div>

                 <AppearanceSettings />
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="shark-card shark-border rounded-2xl border">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-bold text-white">Уведомления</h4>
                      <p className="text-gray-400 text-sm">Управление звуками и всплывающими уведомлениями</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" checked={notifEnabled} onChange={(e) => setNotifEnabled(e.target.checked)} />
                      <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                <div className="shark-card shark-border rounded-2xl border">
                  <h4 className="text-lg font-bold text-white mb-4">🔊 Звук сообщений</h4>
                  <div className="flex items-center gap-3">
                    <select value={soundMessage} onChange={(e) => setSoundMessage(e.target.value)} className="flex-1 bg-[#0f1115] text-white border border-gray-700 rounded-xl px-4 py-2.5 text-sm">
                      <option value="default">По умолчанию</option>
                      <option value="gentle">Нежный</option>
                      <option value="pop">Поп</option>
                      <option value="classic">Классический</option>
                      <option value="custom">Свой звук</option>
                      <option value="none">Без звука</option>
                    </select>
                    <button onClick={() => testSound(soundMessage)} className="px-3 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-xl text-white text-sm transition border border-gray-700">▶ Тест</button>
                  </div>
                </div>

                <div className="shark-card shark-border rounded-2xl border">
                  <h4 className="text-lg font-bold text-white mb-4">❤️ Звук лайков</h4>
                  <div className="flex items-center gap-3">
                    <select value={soundLike} onChange={(e) => setSoundLike(e.target.value)} className="flex-1 bg-[#0f1115] text-white border border-gray-700 rounded-xl px-4 py-2.5 text-sm">
                      <option value="default">По умолчанию</option>
                      <option value="gentle">Нежный</option>
                      <option value="pop">Поп</option>
                      <option value="classic">Классический</option>
                      <option value="custom">Свой звук</option>
                      <option value="none">Без звука</option>
                    </select>
                    <button onClick={() => testSound(soundLike)} className="px-3 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-xl text-white text-sm transition border border-gray-700">▶ Тест</button>
                  </div>
                </div>

                <div className="shark-card shark-border rounded-2xl border">
                  <h4 className="text-lg font-bold text-white mb-4">🎵 Свой звук</h4>
                  <p className="text-gray-400 text-sm mb-4">Загрузите свой MP3 или WAV файл</p>
                  <input type="file" accept="audio/mp3,audio/wav,audio/mpeg" onChange={handleCustomSoundUpload} className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-blue-600 file:text-white hover:file:bg-blue-500 cursor-pointer" />
                  {customSound && (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-green-400 text-sm">✓ Свой звук загружен</span>
                      <button onClick={() => { localStorage.removeItem('custom_sound'); setCustomSound(null); }} className="text-red-400 text-xs hover:text-red-300">Удалить</button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'pet' && (
              <PetSettings />
            )}

            {activeTab === 'language' && (
               <div className="space-y-2">
                  <LangOption code="ru" name="Русский" flag="🇷🇺" active />
                  <LangOption code="en" name="English" flag="🇬🇧" />
                  <LangOption code="de" name="Deutsch" flag="🇩🇪" />
                  <LangOption code="es" name="Español" flag="🇪" />
               </div>
            )}

            {activeTab === 'security' && (
              <SecuritySettings
                securityOperation={securityOperation}
                acquireSecurityOperation={acquireSecurityOperation}
                releaseSecurityOperation={releaseSecurityOperation}
                onCurrentSessionRevoked={onCurrentSessionRevoked}
                pendingRecoveryCodes={pendingRecoveryCodes}
                publishRecoveryCodes={publishRecoveryCodes}
                acknowledgeRecoveryCodes={acknowledgeRecoveryCodes}
              />
            )}

            {activeTab === 'support' && (
              <div className="text-center py-10">
                 <div className="text-6xl mb-4">🦈</div>
                 <h4 className="text-xl font-bold text-white mb-2">Нужна помощь?</h4>
                 <p className="text-gray-400 mb-6">Наша команда поддержки всегда готова ответить на ваши вопросы.</p>
                 <button className="shark-accent-button px-6 py-3 text-white rounded-xl font-bold transition">Написать в поддержку</button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

const LangOption = ({ code, name, flag, active }: { code: string, name: string, flag: string, active?: boolean }) => (
  <button className={`shark-density-control w-full flex items-center gap-4 rounded-2xl border transition ${active ? 'bg-blue-600/20 border-blue-500/50' : 'bg-[#15171c] border-gray-800 hover:bg-gray-800'}`}>
     <span className="text-2xl">{flag}</span>
     <div className="flex-1 text-left">
        <p className="font-bold text-white">{name}</p>
        <p className="text-xs text-gray-500 uppercase">{code}</p>
     </div>
     {active && <span className="text-blue-400 font-bold">✓</span>}
  </button>
);

export default SettingsModal;
